/**
 * ProposalTotals - Resumo financeiro da proposta
 *
 * Exibe subtotal, economia (desconto), recorrência e total com tipografia
 * de destaque para reforçar a ancoragem de valor.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-11 v1.0.0 - Criação inicial (redesign premium da landing /p/:token)
 */

import React from 'react';
import { Repeat, Sparkles } from 'lucide-react';
import { formatCurrency } from './proposalFormat';

interface ProposalTotalsProps {
  subtotal: number;
  discountTotal: number;
  mrrTotal: number;
  total: number;
  currency: string;
}

export function ProposalTotals({
  subtotal,
  discountTotal,
  mrrTotal,
  total,
  currency,
}: ProposalTotalsProps) {
  return (
    <section className="proposal-surface proposal-reveal overflow-hidden">
      <div className="space-y-2.5 px-5 py-5 md:px-7">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Subtotal</span>
          <span>{formatCurrency(subtotal, currency)}</span>
        </div>

        {Number(discountTotal) > 0 && (
          <div className="flex items-center justify-between text-sm font-medium text-emerald-600">
            <span className="inline-flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" />
              Desconto aplicado
            </span>
            <span>- {formatCurrency(discountTotal, currency)}</span>
          </div>
        )}

        {Number(mrrTotal) > 0 && (
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Repeat className="h-3.5 w-3.5" />
              Recorrência mensal
            </span>
            <span>{formatCurrency(mrrTotal, currency)}</span>
          </div>
        )}
      </div>

      <div className="flex items-end justify-between border-t bg-secondary/60 px-5 py-5 md:px-7">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
            Total da proposta
          </p>
          {Number(mrrTotal) > 0 && (
            <p className="text-xs text-muted-foreground">
              + {formatCurrency(mrrTotal, currency)} por mês
            </p>
          )}
        </div>
        <p className="text-2xl font-semibold tracking-tight md:text-3xl">
          {formatCurrency(total, currency)}
        </p>
      </div>
    </section>
  );
}
